import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tabs, Tab, makeStyles } from '@material-ui/core';
import { GridColDef, GridRenderEditCellParams, GridRowId, useGridApiContext } from '@mui/x-data-grid-pro';
import { Stack, Typography, Alert, Select, MenuItem, FormControl, InputLabel, Autocomplete, TextField } from '@mui/material';

import { CopilotStore } from '../../CopilotStore';
import { TagConfig, Tag, TagValueGroupUpsertItem } from '../../models';
import { TagManagementButtons } from './TagManagementButtons';
import { TagManagementDataGrid } from './TagManagementDataGrid';
import { TagManagementDocTags } from './TagManagementDocTags';
import StepConfirmationWithState, { ConfirmationDialogPropsWithState } from '../../../controls/StepConfirmationWithState';
import { buildTagConfigByIndex, normalizeTagLabel } from './tagConfigLookups';

const useStyles = makeStyles((theme) => ({
    tabs: {
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e0e0e0',
        '& .MuiTab-root': {
            backgroundColor: '#ffffff',
            '&.Mui-selected': {
                backgroundColor: '#ffffff',
            },
        },
        '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    nonEditableCell: {
        backgroundColor: '#f5f5f5 !important',
    },
}));

type TagManagementProps = {
    store: CopilotStore
}

type MultiSelectTagValueEditorProps = {
    params: GridRenderEditCellParams;
    cfg: TagConfig;
    tagValueOptions: Tag[];
    valueLookup: Map<string, Tag>;
};

const SELECT_ALL_LABEL = 'Select All';
const SELECT_ALL_ROWID = '__select_all__';
const GROUP_VALUE_SEPARATOR = '*|*';

const MultiSelectTagValueEditor: React.FC<MultiSelectTagValueEditorProps> = ({
    params, cfg, tagValueOptions, valueLookup,
}) => {
    const apiRef = useGridApiContext();
    const tagIndex = typeof cfg.tag_index === 'string' ? parseInt(cfg.tag_index, 10) : cfg.tag_index;

    const optionsWithAll = useMemo<Tag[]>(
        () => tagValueOptions.length > 0
            ? [{ rowid: SELECT_ALL_ROWID, value: SELECT_ALL_LABEL, tag_index: tagIndex } as Tag, ...tagValueOptions]
            : tagValueOptions,
        [tagValueOptions, tagIndex],
    );

    const initial = useMemo<Tag[]>(() => {
        const cell = String(params.value ?? '');
        if (!cell.trim()) return [];
        return cell
            .split(GROUP_VALUE_SEPARATOR)
            .map(t => t.trim())
            .filter(Boolean)
            .map(token => valueLookup.get(`${tagIndex}|${token.toLowerCase()}`))
            .filter((tv): tv is Tag => tv !== undefined);
    }, [params.value, tagIndex, valueLookup]);

    const [selected, setSelected] = useState<Tag[]>(initial);

    const handleChange = (_e: React.SyntheticEvent, newValue: Tag[]) => {
        const hasSentinel = newValue.some(tv => tv.rowid === SELECT_ALL_ROWID);
        const next = hasSentinel
            ? (selected.length === tagValueOptions.length ? [] : tagValueOptions)
            : newValue;
        setSelected(next);
        const joined = next.map(tv => tv.value ?? '').filter(Boolean).join(GROUP_VALUE_SEPARATOR);
        apiRef.current.setEditCellValue({ id: params.id, field: params.field, value: joined });
    };

    return (
        <Autocomplete
            multiple
            disableCloseOnSelect
            size='small'
            sx={{ width: '100%' }}
            options={optionsWithAll}
            value={selected}
            onChange={handleChange}
            getOptionLabel={(opt) => opt.value ?? ''}
            isOptionEqualToValue={(a, b) => a.rowid === b.rowid}
            renderOption={(props, option) => (
                <li {...props} key={option.rowid}>
                    {option.rowid === SELECT_ALL_ROWID ? <strong>{SELECT_ALL_LABEL}</strong> : option.value}
                </li>
            )}
            renderInput={(p) => <TextField {...p} variant='standard' autoFocus />}
        />
    );
};

export const TagManagement = observer(({ store }: TagManagementProps) => {
    const classes = useStyles();
    const {
        selectedCustomer: customer,
        tagConfigs,
        tagValues,
        tagValueGroups,
        fetchTagConfigs,
        upsertTagConfigs,
        fetchTagValues,
        upsertTagValues,
        fetchTagValueGroups,
        upsertTagValueGroups,
        deleteTagValueGroups,
        tagValueByRowId,
        activeTagValuesByTagIndex,
        tagValuesByTagIndexAndValue,
        tagConfigByIndex,
        tagConfigByLabel,
        tagValuesLoading,
    } = store

    // Main tab state
    const [tabValue, setTabValue] = useState(0);

    // Tag Config Management state
    const [configsLoading, setConfigsLoading] = useState<boolean>(false);
    const [localTagConfigs, setLocalTagConfigs] = useState<TagConfig[]>([]);
    const [configsEdited, setConfigsEdited] = useState<TagConfig[]>([]);

    // Tag Values Management state
    const [valuesLoading, setValuesLoading] = useState<boolean>(false);
    const [valuesEditedById, setValuesEditedById] = useState<Map<string, Tag>>(() => new Map());
    const [selectedValueIds, setSelectedValueIds] = useState<GridRowId[]>([]);
    
    // Tag Configs Management state
    const [selectedConfigId, setSelectedConfigId] = useState<GridRowId | null>(null);

    // Tag Groups Management state
    const [groupsLoading, setGroupsLoading] = useState<boolean>(false);
    const [selectedGroupRowIds, setSelectedGroupRowIds] = useState<GridRowId[]>([]);
    // Local rendering state for the grid.
    const [localTagGroupRows, setLocalTagGroupRows] = useState<any[]>([]);
    // Single bucket of touched rows: existing groups (keyed by numeric _id)
    // and new rows (keyed by `temp_*` _id). Mirrors valuesEdited/configsEdited.
    const [groupsEdited, setGroupsEdited] = useState<any[]>([]);

    const [dialogProps, setDialogProps] = useState<ConfirmationDialogPropsWithState>();
    
    const [tempIndex, setTempIndex] = useState<number>(0);
    const getTempIndex = () => {
        setTempIndex(tempIndex + 1);
        return tempIndex;
    }
    const tagValuesBusy = valuesLoading || tagValuesLoading;

    const showInfoDialog = (title: string, text?: string, alertText?: string) => {
        setDialogProps({
            title,
            content: () => (
                <div>
                    {alertText && (
                        <Alert severity="error" style={{ marginBottom: 16, whiteSpace: 'pre-line' }}>
                            {alertText}
                        </Alert>
                    )}
                    {text && (
                        <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                            {text}
                        </Typography>
                    )}
                </div>
            ),
            onClose: () => setDialogProps(undefined),
            closeLabel: 'OK'
        });
    };

    // Load data when customer changes
    useEffect(() => {
        loadTagConfigs();
        loadTagValueGroups();
    }, [customer]);

    // Sync local configs with store when store configs change
    useEffect(() => {
        setLocalTagConfigs([...tagConfigs]);
    }, [tagConfigs]);

    const valuesEdited = useMemo(() => Array.from(valuesEditedById.values()), [valuesEditedById]);

    const localTagValues = useMemo(() => {
        // Sort by updated_at descending (newest first), then overlay unsaved edits.
        const sortedValues = [...tagValues].sort((a, b) => {
            if (!a.updated_at && !b.updated_at) return 0;
            if (!a.updated_at) return 1;
            if (!b.updated_at) return -1;
            return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
        });

        const newValues: Tag[] = [];
        valuesEditedById.forEach((editedValue, rowid) => {
            if (String(rowid).includes('temp')) {
                newValues.push(editedValue);
            }
        });

        return [
            ...newValues,
            ...sortedValues.map(value => valuesEditedById.get(value.rowid) ?? value),
        ];
    }, [tagValues, valuesEditedById]);

    const localTagValuesByTagIndexAndValue = useMemo(() => {
        const lookup = new Map<string, Tag[]>();
        localTagValues.forEach(tagValue => {
            const key = `${tagValue.tag_index}|${(tagValue.value ?? '').trim().toLowerCase()}`;
            const values = lookup.get(key) ?? [];
            values.push(tagValue);
            lookup.set(key, values);
        });
        return lookup;
    }, [localTagValues]);

    const localTagConfigByIndex = useMemo(
        () => buildTagConfigByIndex(localTagConfigs),
        [localTagConfigs]);


    const loadTagConfigs = async () => {
        setConfigsEdited([]);
        setSelectedConfigId(null);
        if (!customer?.id) return;
        setConfigsLoading(true);
        try {
            await fetchTagConfigs();
        } catch (error) {
            console.error('Error loading tag configs:', error);
        } finally {
            setConfigsLoading(false);
        }
    };

    const addNewConfig = () => {
        const newConfig: TagConfig = {
            tag_label: '',
            tag_index: `temp_${getTempIndex()}`,
            data_type: 'string',
            match_method: 'equals',
            is_custom: true,
            is_active: true,
            is_mandatory: false,
            is_multiselect: false,
            is_required: false
        };

        // Add to local working copy only
        setLocalTagConfigs([...localTagConfigs, newConfig]);
        setConfigsEdited([...configsEdited, newConfig]);
    };

    const onConfigChanged = (editedConfig: TagConfig) => {
        const updatedConfigs = localTagConfigs.map(config => 
            config.tag_index === editedConfig.tag_index ? editedConfig : config
        );
        setLocalTagConfigs(updatedConfigs);
        
        if (String(editedConfig.tag_index).includes('temp')) {
            setConfigsEdited(prev => [...prev.filter(config => config.tag_index !== editedConfig.tag_index), editedConfig]);
            return;
        }

        // Compare against store values (original configs)
        const originalConfig = tagConfigs.find(orig => orig.tag_index === editedConfig.tag_index);
        if (originalConfig) {
            const hasChanged = (
                editedConfig.tag_label !== originalConfig.tag_label ||
                editedConfig.is_active !== originalConfig.is_active ||
                editedConfig.is_multiselect !== originalConfig.is_multiselect ||
                editedConfig.is_required !== originalConfig.is_required
            );
            
            if (hasChanged) {
                setConfigsEdited(prev => [...prev.filter(config => config.tag_index !== editedConfig.tag_index), editedConfig]);
            } else {
                setConfigsEdited(prev => prev.filter(config => config.tag_index !== editedConfig.tag_index));
            }
        }
    };

    const saveConfigs = async () => {
        if (!customer?.id) return;
        setConfigsLoading(true);
        try {
            // Remove temp indexes - omit tag_index for new configs so backend assigns proper integer indexes
            const configsToSave = configsEdited.map(config => {
                if (String(config.tag_index).startsWith('temp_')) {
                    const { tag_index, ...configWithoutIndex } = config;
                    return configWithoutIndex;
                }
                return config;
            });
            
            await upsertTagConfigs(configsToSave as TagConfig[]);
            setConfigsEdited([]);
            await loadTagConfigs();
        } catch (error) {
            console.error('Error saving tag configs:', error);
        } finally {
            setConfigsLoading(false);
        }
    };

    const getPersonaIndex =() => tagConfigByLabel.get('persona')?.tag_index;

    const isValueProtected = (tagValue: Tag) => {
        const tagConfig = tagConfigByIndex.get(String(tagValue.tag_index));
        if (!tagConfig?.protected_values) return false;
        return tagConfig.protected_values.includes(tagValue.value);
    };

    const getDeactivatedPersonas = (): Tag[] => {
        const personaIndex = getPersonaIndex();
        if (personaIndex === undefined) return [];
        
        return valuesEdited.filter(editedValue => {
            const originalValue = tagValueByRowId.get(editedValue.rowid);
            return (
                editedValue.tag_index === personaIndex &&
                originalValue?.is_active === true &&
                editedValue.is_active === false
            );
        });
    };

    const loadTagValueGroups = async () => {
        setSelectedGroupRowIds([]);
        // Refresh discards any uncommitted local edits/adds.
        setGroupsEdited([]);
        if (!customer?.id) return;
        setGroupsLoading(true);
        try {
            await fetchTagValueGroups();
        } catch (error) {
            console.error('Error loading tag value groups:', error);
        } finally {
            setGroupsLoading(false);
        }
    };

    const onGroupRowChanged = (newRow: any) => {
        setGroupsEdited(prev => [
            ...prev.filter(r => String(r._id) !== String(newRow._id)),
            newRow,
        ]);
    };

    const addNewGroupRow = () => {
        if (!customer?.id) return;
        const newRow: Record<string, any> = {
            _id: `temp_${getTempIndex()}`,
            group_id: null,
            member_count: 0,
            created_at: null,
        };
        groupTagConfigs.forEach(cfg => {
            newRow[`tag_${cfg.tag_index}`] = '';
        });
        setGroupsEdited(prev => [newRow, ...prev]);
    };

    // Parse a row's per-tag-config cells into a list of tag_value_ids.
    // Pushes any error strings into `errs` (ref-style accumulator) keyed by `lineNum`.
    // Tag configs in scope for the Tag Groups feature: active and not persona.
    // Persona has its own access-control semantics handled upstream in the
    // tag-values list and must not participate in dependency groups.
    const isGroupableConfig = (cfg: TagConfig) =>
        cfg.is_active !== false &&
        (cfg.tag_label ?? '').trim().toLowerCase() !== 'persona';

    const groupTagConfigs = useMemo(
        () => tagConfigs.filter(isGroupableConfig),
        [tagConfigs],
    );

    // Build the lookups used by both the inline editor and the CSV handler.
    // - labelToConfig: case-insensitive tag_label → TagConfig (for CSV headers).
    // - tagIndexToConfig: numeric tag_index → TagConfig for active configs (for grid rows).
    // - valueLookup: `${tag_index}|${value.toLowerCase()}` → Tag, for O(1) value validation.
    // All three exclude persona so neither CSV import nor inline edit can target it.
    const buildTagLookups = () => {
        const labelToConfig = new Map<string, TagConfig>();
        groupTagConfigs.forEach(c => {
            labelToConfig.set((c.tag_label ?? '').trim().toLowerCase(), c);
        });
        const tagIndexToConfig = new Map<number, TagConfig>();
        groupTagConfigs.forEach(c => {
            const idx = typeof c.tag_index === 'string' ? parseInt(c.tag_index, 10) : c.tag_index;
            tagIndexToConfig.set(idx as number, c);
        });
        const valueLookup = new Map<string, Tag>();
        tagValuesByTagIndexAndValue.forEach((values, key) => {
            valueLookup.set(key, values[values.length - 1]);
        });
        return { labelToConfig, tagIndexToConfig, valueLookup };
    };

    const tagLookups = useMemo(buildTagLookups, [groupTagConfigs, tagValuesByTagIndexAndValue]);

    // Validate one grid row (cells keyed `tag_${tag_index}`) and return its
    // tag_value_ids, accumulating any errors into `errs`.
    const buildIdsFromRow = (
        row: any,
        lineNum: number,
        tagIndexToConfig: Map<number, TagConfig>,
        valueLookup: Map<string, Tag>,
        errs: string[],
    ): string[] => {
        const activeConfigs = Array.from(tagIndexToConfig.values());
        const ids: string[] = [];
        const rowEmpty = activeConfigs.every(cfg => {
            const v = row[`tag_${cfg.tag_index}`];
            return v === undefined || v === null || String(v).trim() === '';
        });
        if (rowEmpty) {
            errs.push(`Row ${lineNum}: empty row.`);
            return ids;
        }
        activeConfigs.forEach(cfg => {
            const tagIndex = typeof cfg.tag_index === 'string' ? parseInt(cfg.tag_index, 10) : cfg.tag_index;
            const cell = row[`tag_${cfg.tag_index}`];
            if (cell === undefined || cell === null || String(cell).trim() === '') return;
            String(cell).split(GROUP_VALUE_SEPARATOR).map(t => t.trim()).filter(Boolean).forEach(token => {
                const tv = valueLookup.get(`${tagIndex}|${token.toLowerCase()}`);
                if (!tv) {
                    errs.push(`Row ${lineNum}, "${cfg.tag_label}": "${token}" is not a known tag value.`);
                } else {
                    ids.push(tv.rowid);
                }
            });
        });
        return ids;
    };

    const saveTagGroups = async () => {
        if (groupsEdited.length === 0 || !customer?.id) return;

        const { tagIndexToConfig, valueLookup } = buildTagLookups();
        const errs: string[] = [];
        const items: TagValueGroupUpsertItem[] = groupsEdited.map((row, idx) => ({
            // null group_id → backend creates a new group; numeric → it's replaced.
            group_id: String(row._id).startsWith('temp_') ? null : String(row.group_id),
            tag_value_ids: buildIdsFromRow(row, idx + 1, tagIndexToConfig, valueLookup, errs),
        }));

        if (errs.length > 0) {
            const preview = errs.slice(0, 10).join('\n');
            const more = errs.length > 10 ? `\n+ ${errs.length - 10} more` : '';
            showInfoDialog('Tag Groups Save Failed', undefined, `${preview}${more}`);
            return;
        }

        setGroupsLoading(true);
        try {
            const res = await upsertTagValueGroups(items);
            if (!res.success) {
                showInfoDialog('Tag Groups Save Failed', undefined, 'Server reported failure. Local edits kept; try Refresh.');
                return;
            }
            setGroupsEdited([]);
            await fetchTagValueGroups();
            const total = res.groups_created + res.groups_updated;
            showInfoDialog('Tag Groups Saved', `Saved ${total} group${total === 1 ? '' : 's'}.`);
        } catch (e) {
            console.error('Error saving tag value groups:', e);
            showInfoDialog('Tag Groups Save Failed', undefined, 'Failed to save. See console for details.');
        } finally {
            setGroupsLoading(false);
        }
    };

    const handleTagGroupsCSVUpload = async (data: any[], errors: string[]) => {
        if (errors.length > 0) return;
        if (data.length === 0) {
            showInfoDialog('Tag Groups Import Failed', undefined, 'No rows found in the CSV file.');
            return;
        }
        const headers = Object.keys(data[0] ?? {});
        if (headers.length === 0) {
            showInfoDialog('Tag Groups Import Failed', undefined, 'CSV is missing a header row.');
            return;
        }

        const { labelToConfig, tagIndexToConfig, valueLookup } = buildTagLookups();
        const errs: string[] = [];

        // Validate every header maps to a known tag label, and remember the
        // header → config mapping so we can normalize each row into the same
        // shape the inline editor uses (`tag_${tag_index}` keys).
        const headerToConfig = new Map<string, TagConfig>();
        headers.forEach(h => {
            const cfg = labelToConfig.get(h.trim().toLowerCase());
            if (!cfg) {
                errs.push(`Unknown tag label in CSV header: "${h}"`);
            } else {
                headerToConfig.set(h, cfg);
            }
        });

        const items: TagValueGroupUpsertItem[] = headerToConfig.size === headers.length
            ? data.map((row, rowIdx) => {
                const normalized: any = {};
                headerToConfig.forEach((cfg, header) => {
                    normalized[`tag_${cfg.tag_index}`] = row[header];
                });
                // CSV lineNum: +1 for header row, +1 for 1-based indexing.
                return {
                    group_id: null,
                    tag_value_ids: buildIdsFromRow(normalized, rowIdx + 2, tagIndexToConfig, valueLookup, errs),
                };
            })
            : [];

        if (errs.length > 0) {
            const preview = errs.slice(0, 10).join('\n');
            const more = errs.length > 10 ? `\n+ ${errs.length - 10} more` : '';
            showInfoDialog('Tag Groups Import Failed', undefined, `${preview}${more}`);
            return;
        }

        // Phase A passed — every row is valid. Confirm before committing because
        // the upsert wipes existing groups (replace_existing=true).
        const newCount = items.length;
        const existingCount = tagValueGroups.length;
        setDialogProps({
            title: 'Replace All Tag Groups?',
            content: () => (
                <div>
                    <Alert severity='warning' style={{ marginBottom: 16 }}>
                        Uploading this CSV will <strong>replace all {existingCount} existing tag group{existingCount === 1 ? '' : 's'}</strong> for this customer with {newCount} new group{newCount === 1 ? '' : 's'}. This action cannot be undone.
                    </Alert>
                    <Typography variant='body2'>Continue with the upload?</Typography>
                </div>
            ),
            onYes: async () => {
                setDialogProps(undefined);
                setGroupsLoading(true);
                try {
                    const res = await upsertTagValueGroups(items, true);
                    if (!res.success) {
                        showInfoDialog('Tag Groups Import Failed', undefined, 'Server reported failure. No changes were applied.');
                        return;
                    }
                    // Replace-all wipes the server set; any uncommitted local edits are now stale.
                    setGroupsEdited([]);
                    await fetchTagValueGroups();
                    showInfoDialog(
                        'Tag Groups Imported',
                        `Replaced existing tag groups with ${res.groups_created} new group${res.groups_created === 1 ? '' : 's'}.`,
                    );
                } catch (e) {
                    console.error('Error replacing tag value groups:', e);
                    showInfoDialog('Tag Groups Import Failed', undefined, 'Failed to save tag groups. See console for details.');
                } finally {
                    setGroupsLoading(false);
                }
            },
            onClose: () => setDialogProps(undefined),
            yesLabel: 'Upload',
            closeLabel: 'Cancel',
        });
    };

    const handleDeleteTagGroupsClick = () => {
        if (selectedGroupRowIds.length === 0) return;
        setDialogProps({
            title: 'Delete Tag Groups?',
            content: () => (
                <Typography variant='body1'>
                    Delete {selectedGroupRowIds.length} selected tag group{selectedGroupRowIds.length === 1 ? '' : 's'}?
                </Typography>
            ),
            onYes: async () => {
                const allSelectedIds = selectedGroupRowIds.map(id => String(id));
                const realIds = allSelectedIds.filter(id => !id.startsWith('temp_'));
                const selectedSet = new Set(allSelectedIds);

                setGroupsLoading(true);
                try {
                    if (realIds.length > 0) {
                        await deleteTagValueGroups(realIds);
                    }
                    setGroupsEdited(prev =>
                        prev.filter(r => !selectedSet.has(String(r._id))),
                    );
                    if (realIds.length > 0) {
                        await fetchTagValueGroups();
                    }
                    setSelectedGroupRowIds([]);
                } catch (error) {
                    console.error('Error deleting tag value groups:', error);
                } finally {
                    setGroupsLoading(false);
                }
                setDialogProps(undefined);
            },
            onClose: () => setDialogProps(undefined),
            yesLabel: 'Delete',
            closeLabel: 'Cancel',
        });
    };

    const csvEscape = (cell: string): string => {
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
            return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
    };

    const generateTagGroupsCSV = (): string => {
        const headers = groupTagConfigs.map(cfg => cfg.tag_label ?? `Tag ${cfg.tag_index}`);
        const lines: string[] = [headers.map(csvEscape).join(',')];
        // Sort by created_at so the CSV order is stable across exports.
        const sortedGroups = [...tagValueGroups].sort((a, b) => {
            const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
            const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
            return ta - tb;
        });
        sortedGroups.forEach(group => {
            const valuesByTagIndex = new Map<number, string[]>();
            (group.tag_value_ids ?? []).forEach(tvid => {
                const tv = tagValueByRowId.get(tvid);
                if (!tv) return;
                const list = valuesByTagIndex.get(tv.tag_index) ?? [];
                list.push(tv.value ?? '');
                valuesByTagIndex.set(tv.tag_index, list);
            });
            const cells = groupTagConfigs.map(cfg => {
                const idx = typeof cfg.tag_index === 'string' ? parseInt(cfg.tag_index, 10) : cfg.tag_index;
                const vals = valuesByTagIndex.get(idx as number) ?? [];
                return csvEscape(vals.join(GROUP_VALUE_SEPARATOR));
            });
            lines.push(cells.join(','));
        });
        return lines.join('\n');
    };

    const validateTagGroupsDownload = (): string | undefined => {
        if (!tagConfigs || tagConfigs.length === 0) {
            return 'No tag configurations found. Please create tag configurations first.';
        }
        return undefined;
    };

    const loadTagValues = async () => {
        setValuesEditedById(new Map());
        setSelectedValueIds([]);
        if (!customer?.id) return;
        setValuesLoading(true);
        try {
            await fetchTagValues();
        } catch (error) {
            console.error('Error loading tag values:', error);
        } finally {
            setValuesLoading(false);
        }
    };

    const addNewValue = () => {
        setDialogProps(getConfigSelectionDialog());
    };

    const createNewValueWithConfig = (tagIndex: number) => {
        const newValue: Tag = {
            rowid: `temp_${getTempIndex()}`,
            tag_index: tagIndex,
            value: '',
            is_active: true,
            description: ''
        };
        setValuesEditedById(prev => {
            const next = new Map(prev);
            next.set(newValue.rowid, newValue);
            return next;
        });
    };


    const getConfigSelectionDialog = (): ConfirmationDialogPropsWithState => {
        return {
            title: 'Select Tag Configuration',
            content: (state, setState) => {
                const handleConfigChange = (selectedConfig: TagConfig | null) => {
                    setState(selectedConfig ? selectedConfig.tag_index : undefined);
                };

                return (
                    <div>
                        <Typography variant='body2' style={{ marginBottom: 16 }}>
                            Choose a tag configuration for the new tag value:
                        </Typography>

                        {tagConfigs.length > 0 ? (
                            <Autocomplete
                                options={tagConfigs}
                                getOptionLabel={(option) => option.tag_label || `Tag ${option.tag_index}`}
                                value={tagConfigs.find(config => config.tag_index === state) || null}
                                onChange={(event, newValue) => handleConfigChange(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Tag Configuration"
                                        variant="outlined"
                                        style={{ marginTop: 16 }}
                                    />
                                )}
                            />
                        ) : (
                            <Alert severity="warning">
                                No active tag configurations found. Please create tag configurations first.
                            </Alert>
                        )}
                    </div>
                );
            },
            onYes: (state) => {
                if (state === undefined) {
                    alert('Please select a configuration first.');
                    return;
                }
                createNewValueWithConfig(state);
                setDialogProps(undefined);
            },
            onClose: () => {
                setDialogProps(undefined);
            },
            yesLabel: 'Create',
            closeLabel: 'Cancel',
        };
    };

    const onValueChanged = (editedValue: Tag) => {
        if (String(editedValue.rowid).includes('temp')) {
            setValuesEditedById(prev => {
                const next = new Map(prev);
                next.set(editedValue.rowid, editedValue);
                return next;
            });
            return;
        }

        const originalValue = tagValueByRowId.get(editedValue.rowid);
        if (originalValue) {
            const hasChanged = (
                editedValue.value !== originalValue.value ||
                editedValue.is_active !== originalValue.is_active ||
                Boolean(editedValue.description) !== Boolean(originalValue.description)
            );
            
            if (hasChanged) {
                setValuesEditedById(prev => {
                    const next = new Map(prev);
                    next.set(editedValue.rowid, editedValue);
                    return next;
                });
            } else {
                setValuesEditedById(prev => {
                    const next = new Map(prev);
                    next.delete(editedValue.rowid);
                    return next;
                });
            }
        }
    };

    const saveValues = async () => {
        if (!customer?.id) return;
        setValuesLoading(true);
        try {
            const payload = valuesEdited.map((t: Tag) => {
                const copy: Tag = { ...t };
                if (String(copy.rowid).startsWith('temp')) {
                    copy.rowid = "";
                }
                return copy as Tag;
            });
            await upsertTagValues(payload);
            setValuesEditedById(new Map());
            await loadTagValues();
        } catch (error) {
            console.error('Error saving tag values:', error);
        } finally {
            setValuesLoading(false);
        }
    };

    const handleSaveValues = async () => {
        const deactivatedPersonas = getDeactivatedPersonas();
        if (deactivatedPersonas.length > 0) {
            const personaIndex = getPersonaIndex();
            const deactivatedPersonaIds = new Set(deactivatedPersonas.map(tag => tag.rowid));
            const availablePersonaValues = (activeTagValuesByTagIndex.get(Number(personaIndex)) ?? [])
                .filter(tag => !deactivatedPersonaIds.has(tag.rowid));
            setDialogProps(getUpdateUsersPersonaConfirmationDialog(
                deactivatedPersonas, 
                availablePersonaValues, 
                saveValues
            ));
            return;
        }
        saveValues();
        setDialogProps(undefined);
    };

    const isConfigProtected = (config: TagConfig) => {
        if (!config) return true; // Treat missing configs as protected
        return !config.is_custom;
    };


    const handleBulkUpload = (data: any[], errors: string[]) => {
        if (errors.length > 0) {
            // Errors are already shown by the CSVUploadButton component
            return;
        }

        if (data.length === 0) {
            showInfoDialog('Error', undefined, 'No valid data found in CSV file.');
            return;
        }

        // Validate that all tag labels exist before processing
        const invalidTags: string[] = [];
        const validRows: any[] = [];

        data.forEach((row, index) => {
            const tagLabel = row.tag_label?.trim();
            if (!tagLabel) {
                invalidTags.push(`Row ${index + 2}: Empty tag label`);
                return;
            }

            // Find the tag configuration by label (case-insensitive)
            const tagConfig = tagConfigByLabel.get(normalizeTagLabel(tagLabel));

            if (!tagConfig) {
                invalidTags.push(`Row ${index + 2}: Tag label "${tagLabel}" not found`);
                return;
            }

            validRows.push({ ...row, tagConfig });
        });

        // If there are validation errors, show them and stop processing
        if (invalidTags.length > 0) {
            const errorMessage = `CSV validation failed:\n${invalidTags.join('\n')}\nPlease fix these errors and try again.`;
            showInfoDialog('CSV Validation Failed', undefined, errorMessage);
            return;
        }

        // Check for duplicates and filter out existing tag values
        const newTags: Tag[] = [];
        const duplicateValues: string[] = [];
        const skippedRows: number[] = [];

        validRows.forEach((row, index) => {
            const tagValue = row.value?.trim() || '';
            const tagIndex = row.tagConfig.tag_index as number;
            
            // Check if this tag value already exists (case-insensitive, trimmed)
            const existingTag = localTagValuesByTagIndexAndValue.get(`${tagIndex}|${tagValue.toLowerCase()}`)?.[0];

            if (existingTag) {
                duplicateValues.push(`"${row.tagConfig.tag_label}": "${tagValue}"`);
                skippedRows.push(index + 2); // +2 because CSV has header and we're 0-indexed
                return;
            }

            newTags.push({
                rowid: `temp_bulk_${getTempIndex()}_${index}`,
                tag_index: tagIndex,
                value: tagValue,
                is_active: row.is_active !== undefined ? row.is_active : true,
                description: row.description?.trim() || ''
            });
        });

        // Helper function to format duplicate values for display
        const formatDuplicateValues = (duplicates: string[]) => {
            if (duplicates.length <= 2) {
                return duplicates.join('\n');
            } else {
                return `${duplicates[0]}\n${duplicates[1]}\n+ ${duplicates.length - 2} more`;
            }
        };

        // Handle the case where no new tags are found
        if (newTags.length === 0) {
            if (duplicateValues.length > 0) {
                const alertText = `The following values were skipped as they already exist:\n${formatDuplicateValues(duplicateValues)}`;
                showInfoDialog('No New Values', 'No new tag values were imported. All values in the CSV already exist in the system.', alertText);
            } else {
                showInfoDialog('No New Values', 'No new tag values found. All values in the CSV already exist in the system.');
            }
            return;
        }

        // Add new tags to local state
        setValuesEditedById(prev => {
            const next = new Map(prev);
            newTags.forEach(tag => next.set(tag.rowid, tag));
            return next;
        });

        // Show success message with duplicate info if any
        const successMessage = `Successfully imported ${newTags.length} new tag values. Click Save to persist the changes.`;
        
        if (duplicateValues.length > 0) {
            const alertText = `The following values were skipped as they already exist:\n${formatDuplicateValues(duplicateValues)}`;
            showInfoDialog('Import Successful', successMessage, alertText);
        } else {
            showInfoDialog('Import Successful', successMessage);
        }
    };

    const generateTagValuesCSV = (): string => {
        // Create CSV header
        let csvContent = 'tag_label,value,is_active,description';
        
        localTagValues.forEach(tagValue => {
            const tagConfig = tagConfigByIndex.get(String(tagValue.tag_index));
            const tagLabel = tagConfig?.tag_label || `Tag ${tagValue.tag_index}`;
            
            csvContent += `\n"${tagLabel}","${tagValue.value}",${tagValue.is_active},"${tagValue.description || ''}"`;
        });

        return csvContent;
    };

    const validateTagValuesDownload = (): string | undefined => {
        if (!tagConfigs || tagConfigs.length === 0) {
            return 'No tag configurations found. Please create tag configurations first.';
        }
        return undefined;
    };

    const handleCSVTagValueSelection = (csvData: any[], errors: string[]) => {
        if (errors.length > 0) {
            return;
        }

        setSelectedValueIds([]);

        if (csvData.length === 0) {
            showInfoDialog(
                'No Valid Rows',
                'No rows were found in the CSV file.',
            );
            return;
        }

        const matched: Tag[] = [];
        const notFoundRows = new Set<string>();
        const unknownLabels = new Set<string>();
        const ambiguousRows = new Set<string>();

        csvData.forEach((row) => {
            const tagLabel = String(row.tag_label ?? '').trim();
            const rawValue = String(row.value ?? '').trim();
            if (!tagLabel || !rawValue) {
                return;
            }

            const tagConfig = tagConfigByLabel.get(normalizeTagLabel(tagLabel));
            if (!tagConfig) {
                unknownLabels.add(tagLabel);
                return;
            }

            const tagIndex = tagConfig.tag_index;
            const candidates = localTagValuesByTagIndexAndValue.get(`${tagIndex}|${rawValue.toLowerCase()}`) ?? [];

            const rowKey = `${tagLabel} / ${rawValue}`;
            if (candidates.length === 0) {
                notFoundRows.add(rowKey);
                return;
            }

            if (candidates.length > 1) {
                ambiguousRows.add(rowKey);
            }
            matched.push(...candidates);
        });

        const seenMatchedIds = new Set<string>();
        const uniqueMatched = matched.filter((tag) => {
            if (seenMatchedIds.has(tag.rowid)) return false;
            seenMatchedIds.add(tag.rowid);
            return true;
        });

        const selectable: Tag[] = [];
        const skippedProtected: Tag[] = [];
        uniqueMatched.forEach((tag) => {
            if (isValueProtected(tag)) {
                skippedProtected.push(tag);
            } else {
                selectable.push(tag);
            }
        });

        const warningContent = (
            <div>
                {unknownLabels.size > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <Typography variant="subtitle2" style={{ color: '#f44336', marginBottom: 8 }}>
                            Unknown tag labels (not in tag configs):
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {Array.from(unknownLabels).map((label) => (
                                <li key={label}>{label}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {ambiguousRows.size > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <Typography variant="subtitle2" style={{ color: '#ff9800', marginBottom: 8 }}>
                            Multiple tag values matched these rows (all matches will be selected):
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {Array.from(ambiguousRows).map((key) => (
                                <li key={key}>{key}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {skippedProtected.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <Typography variant="subtitle2" style={{ color: '#2196f3', marginBottom: 8 }}>
                            Protected tag values cannot be selected:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {skippedProtected.map((t) => (
                                <li key={t.rowid}>{t.value || t.rowid}</li>
                            ))}
                        </ul>
                    </div>
                )}

                {notFoundRows.size > 0 && (
                    <div style={{ marginBottom: 16 }}>
                        <Typography variant="subtitle2" style={{ color: '#f44336', marginBottom: 8 }}>
                            No matching tag value for tag_label / value:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {Array.from(notFoundRows).map((key) => (
                                <li key={key}>{key}</li>
                            ))}
                        </ul>
                    </div>
                )}

                <Typography variant="body1" style={{ marginTop: 16 }}>
                    {selectable.length > 0
                        ? `${selectable.length} tag value(s) will be selected. Do you want to proceed?`
                        : 'No tag values will be selected.'}
                </Typography>
            </div>
        );

        if (selectable.length > 0) {
            setDialogProps({
                title: 'CSV Selection Results',
                content: () => warningContent,
                onYes: () => {
                    setSelectedValueIds(selectable.map((t) => t.rowid));
                    setDialogProps(undefined);
                },
                yesLabel: 'Select Tag Values',
                onClose: () => setDialogProps(undefined),
                closeLabel: 'Cancel',
            });
        } else {
            setDialogProps({
                title: 'No Tag Values Selected',
                content: () => warningContent,
                onClose: () => setDialogProps(undefined),
                closeLabel: 'OK',
            });
        }
    };

    const updateUsersPersona = async (state: any): Promise<boolean> => {
        if (!customer?.id || !state) return false;
        setValuesLoading(true);
        try {
            for (const [fromPersonaRowId, toPersonaRowId] of Object.entries(state)) {
                if (toPersonaRowId && typeof toPersonaRowId === 'string') {
                    await store.updateUsersPersona(toPersonaRowId,fromPersonaRowId);
                }
            }
            return true;
        } catch (error) {
            console.error('Failed to update user personas:', error);
            return false;
        } finally {
            setValuesLoading(false);
        }
    };

    const getPersonaUpdateErrorDialog = (): ConfirmationDialogPropsWithState => {
        return {
            title: 'Error Updating Users',
            content: () => (
                <div>
                    <Typography variant='body1' style={{ marginBottom: 15 }}>
                        Failed to update users' persona assignments. The tag values will not be deleted/de-activated.
                    </Typography>
                    <Typography variant='body1' style={{ marginBottom: 15 }}>
                        Please try again or contact support if the issue persists.
                    </Typography>
                </div>
            ),
            onClose: () => setDialogProps(undefined),
            closeLabel: 'OK',
        };
    };

    const getUpdateUsersPersonaConfirmationDialog = (selectedPersonas: Tag[], availablePersonas: Tag[], onYes: () => void): ConfirmationDialogPropsWithState => {
        return {
            title: 'Update Users Persona',
            content: (state, setState) => {
                const handleSelectChange = (personaRowId: string, value: string) => {
                    const newState = { ...(state || {}) };
                    newState[personaRowId] = value;
                    setState(newState);
                };

                return (
                    <div>
                        <Typography variant='body1' style={{ marginBottom: 15 }}>
                            Deactivating/deleting personas requires users to be reassigned to a new persona.
                        </Typography>
                        <Typography variant='body1' style={{ marginBottom: 15 }}>
                            Please select new persons to replace the following personas:
                        </Typography>
                        
                        {/* Column Headers */}
                        <Stack direction='row' spacing={2} style={{ marginBottom: 10 }}>
                            <div style={{ flex: 1 }}>
                                <Typography variant='subtitle2' style={{ fontWeight: 'bold' }}>
                                    Current
                                </Typography>
                            </div>
                            <div style={{ flex: 1 }}>
                                <Typography variant='subtitle2' style={{ fontWeight: 'bold' }}>
                                    Replacement
                                </Typography>
                            </div>
                        </Stack>

                        {/* Persona Rows */}
                        <div style={{ marginBottom: 15 }}>
                            {selectedPersonas.map(persona => 
                            <Stack direction='row' spacing={2} alignItems='center' key={persona.rowid} style={{ marginBottom: 16 }}>
                                <div style={{ flex: 1 }}>
                                    <Typography variant='body1'>{persona.value}</Typography>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <Autocomplete
                                        options={availablePersonas}
                                        getOptionLabel={(option) => option.value}
                                        value={availablePersonas.find(tag => tag.rowid === (state && state[persona.rowid])) || null}
                                        onChange={(event, newValue) => {
                                            handleSelectChange(persona.rowid, newValue ? newValue.rowid : "");
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Select Replacement"
                                                variant="outlined"
                                                size="small"
                                            />
                                        )}
                                    />
                                </div>
                            </Stack>
                            )}
                        </div>
                    </div>
                );
            },
            onYes: async (state) => {
                const success = await updateUsersPersona(state);
                if (success) {
                    onYes();
                    setDialogProps(undefined);
                } else {
                    // Show error dialog and keep current dialog open
                    setDialogProps(getPersonaUpdateErrorDialog());
                }
            },
            onClose: () => setDialogProps(undefined),
            yesLabel: 'Done',
            closeLabel: 'Cancel',
            yesDisabled: (state) => {
                // Disable yes button if any persona doesn't have a replacement selected
                return selectedPersonas.some(persona => 
                    !state || !state[persona.rowid] || state[persona.rowid] === ""
                );
            },
        };
    };

    // Tag Config columns
    const configColumns: GridColDef[] = [
        {
            field: 'tag_index',
            headerName: 'Index',
            width: 150,
            editable: false,
            cellClassName: classes.nonEditableCell,
        },
        {
            field: 'tag_label',
            headerName: 'Label',
            width: 200,
            editable: true,
            cellClassName: (params) => {
                let classNames = [];
                if (params.value === '' || params.value === null) {
                    classNames.push('error-cell');
                }
                if (!params.row.is_custom) {
                    classNames.push(classes.nonEditableCell);
                }
                return classNames.join(' ');
            }
        },
        {
            field: 'data_type',
            headerName: 'Data Type',
            width: 150,
            editable: false,
            type: 'singleSelect',
            cellClassName: classes.nonEditableCell,
        },
        {
            field: 'match_method',
            headerName: 'Match Method',
            width: 150,
            editable: false,
            type: 'singleSelect',
            cellClassName: classes.nonEditableCell,
        },
        {
            field: 'is_custom',
            headerName: 'Custom',
            width: 150,
            type: 'boolean',
            editable: false,
            cellClassName: classes.nonEditableCell,
        },
        {
            field: 'is_active',
            headerName: 'Active',
            width: 150,
            type: 'boolean',
            editable: true,
            cellClassName: (params) => {
                return params.row.is_custom ? '' : classes.nonEditableCell;
            },
        },
        {
            field: 'is_mandatory',
            headerName: 'Mandatory',
            width: 150,
            type: 'boolean',
            editable: false,
            cellClassName: classes.nonEditableCell,
        },
        {
            field: 'is_required',
            headerName: 'Required',
            width: 150,
            type: 'boolean',
            editable: true,
            cellClassName: (params) => {
                return params.row.is_custom ? '' : classes.nonEditableCell;
            },
        },
        {
            field: 'is_multiselect',
            headerName: 'Multi-select',
            width: 150,
            type: 'boolean',
            editable: true,
            cellClassName: (params) => {
                return params.row.is_custom ? '' : classes.nonEditableCell;
            },
        },
        {
            field: 'updated_at',
            headerName: 'Updated At',
            flex: 1,
            type: 'dateTime',
            editable: false,
            cellClassName: classes.nonEditableCell,
            valueFormatter: (params) => {
                if (!params.value) return '';
                if (typeof params.value === 'boolean') return '';
                if (typeof params.value === 'object' && !(params.value instanceof Date)) return '';
                const date = new Date(params.value as string | number | Date);
                return date.toLocaleString();
            },
        }
    ];

    const isConfigCellEditableCustom = (params: any) => {
        if (!params.row.is_custom) return false;
        return true;
    };

    // Tag Values columns
    const valueColumns: GridColDef[] = [
        {
            field: 'rowid',
            headerName: 'ID',
            width: 150,
            editable: false,
            cellClassName: classes.nonEditableCell,
        },
        {
            field: 'tag_index',
            headerName: 'Tag Config',
            width: 200,
            editable: false,
            cellClassName: classes.nonEditableCell,
            valueGetter: (params) => {
                const tagConfig = localTagConfigByIndex.get(String(params.value));
                return tagConfig?.tag_label || `Missing Tag Index`;
            },
        },
        {
            field: 'value',
            headerName: 'Value',
            flex: 1,
            editable: true,
            type: 'string',
            cellClassName: (params) => {
                let classNames = [];
                if (params.value === '' || params.value === null) {
                    classNames.push('error-cell');
                }
                if (isValueProtected(params.row as Tag)) {
                    classNames.push(classes.nonEditableCell);
                }
                return classNames.join(' ');
            },
        },
        {
            field: 'is_active',
            headerName: 'Active',
            width: 120,
            type: 'boolean',
            editable: true,
            cellClassName: (params) => {
                return isValueProtected(params.row as Tag) ? classes.nonEditableCell : '';
            },
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 1,
            editable: true,
            type: 'string',
            cellClassName: (params) => {
                return isValueProtected(params.row as Tag) ? classes.nonEditableCell : '';
            },
        },
        {
            field: 'updated_at',
            headerName: 'Updated At',
            width: 180,
            type: 'dateTime',
            editable: false,
            cellClassName: classes.nonEditableCell,
            valueFormatter: (params) => {
                if (!params.value) return '';
                if (typeof params.value === 'boolean') return '';
                if (typeof params.value === 'object' && !(params.value instanceof Date)) return '';
                const date = new Date(params.value as string | number | Date);
                return date.toLocaleString();
            },
        },
    ];

    const tagGroupColumns: GridColDef[] = [
        {
            field: 'group_id',
            headerName: 'Group ID',
            width: 150,
            editable: false,
            cellClassName: classes.nonEditableCell,
            valueFormatter: (params) => {
                if (params.value === null || params.value === undefined) return 'NEW';
                return String(params.value);
            },
        },
        ...groupTagConfigs.map<GridColDef>(cfg => ({
            field: `tag_${cfg.tag_index}`,
            headerName: cfg.tag_label ?? `Tag ${cfg.tag_index}`,
            flex: 1,
            editable: true,
            type: 'string',
            renderEditCell: (params) => (
                <MultiSelectTagValueEditor
                    params={params}
                    cfg={cfg}
                    tagValueOptions={activeTagValuesByTagIndex.get(
                        typeof cfg.tag_index === 'string' ? parseInt(cfg.tag_index, 10) : cfg.tag_index,
                    ) ?? []}
                    valueLookup={tagLookups.valueLookup}
                />
            ),
        })),
        {
            field: 'member_count',
            headerName: 'Count',
            width: 90,
            type: 'number',
            editable: false,
            cellClassName: classes.nonEditableCell,
        },
        {
            field: 'created_at',
            headerName: 'Created At',
            width: 180,
            type: 'dateTime',
            editable: false,
            cellClassName: classes.nonEditableCell,
            valueFormatter: (params) => {
                if (!params.value) return '';
                const d = new Date(params.value as string);
                return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
            },
        },
    ];

    // Build localTagGroupRows from store + uncommitted edits. Edits keyed by
    // _id win over the server snapshot; rows with `temp_*` _id are new and
    // pinned to the top.
    useEffect(() => {
        const editsById = new Map(groupsEdited.map(r => [String(r._id), r]));
        const baseRows: any[] = tagValueGroups.map(group => {
            const valuesByTagIndex = new Map<number, string[]>();
            (group.tag_value_ids ?? []).forEach(tvid => {
                const tv = tagValueByRowId.get(tvid);
                if (!tv) return;
                const list = valuesByTagIndex.get(tv.tag_index) ?? [];
                list.push(tv.value ?? '');
                valuesByTagIndex.set(tv.tag_index, list);
            });
            const row: Record<string, any> = {
                _id: String(group.group_id),
                group_id: group.group_id,
                member_count: group.member_count ?? group.tag_value_ids.length,
                created_at: group.created_at,
            };
            groupTagConfigs.forEach(cfg => {
                const idx = typeof cfg.tag_index === 'string' ? parseInt(cfg.tag_index, 10) : cfg.tag_index;
                row[`tag_${cfg.tag_index}`] = (valuesByTagIndex.get(idx as number) ?? []).join(GROUP_VALUE_SEPARATOR);
            });
            return row;
        });
        const withEdits = baseRows.map(row => editsById.get(String(row._id)) ?? row);
        const adds = groupsEdited.filter(r => String(r._id).startsWith('temp_'));
        setLocalTagGroupRows([...adds, ...withEdits]);
    }, [tagValueGroups, groupTagConfigs, tagValueByRowId, groupsEdited]);

    const isValueCellEditableCustom = (params: any) => {
        return !isValueProtected(params.row as Tag)
    };

    const getAddConfigDisabledTooltip = () => {
        if (!customer?.id) return undefined;
        if (localTagConfigs.length >= 6) return 'Maximum of 6 tag configurations allowed';
        return undefined;
    };

    const getSaveConfigsDisabledTooltip = () => {
        if (!customer?.id) return undefined;
        if (configsEdited.length === 0) return 'No changes to save';
        if (configsEdited.find(config => config.tag_label === '' || config.tag_label === null)) return 'Label is required';
        
        // Check for duplicates
        const errorMessage = getSaveConfigsErrorMessage();
        if (errorMessage) return errorMessage;
        
        return undefined;
    };
    

    const getSaveConfigsErrorMessage = () => {
        if (!customer?.id) return undefined;
        
        // Check for empty labels
        const emptyConfigs = configsEdited.filter(config => config.tag_label === '' || config.tag_label === null);
        if (emptyConfigs.length > 0) {
            return `${emptyConfigs.length} tag config(s) have empty labels`;
        }
        
        // Check for duplicate labels within edited configs
        const editedLabels = configsEdited.map(config => config.tag_label?.trim()).filter(Boolean);
        const duplicateLabels = editedLabels.filter((label, index) => editedLabels.indexOf(label) !== index);
        if (duplicateLabels.length > 0) {
            return `Duplicate labels found in edited configs: ${Array.from(new Set(duplicateLabels)).join(', ')}`;
        }
        
        // Check for labels that already exist in non-edited configs
        const existingLabels = localTagConfigs
            .filter(config => !configsEdited.find(edited => edited.tag_index === config.tag_index))
            .map(config => config.tag_label?.trim())
            .filter(Boolean);
        
        const conflictingLabels = configsEdited
            .filter(config => config.tag_label && existingLabels.includes(config.tag_label.trim()))
            .map(config => config.tag_label);
            
        if (conflictingLabels.length > 0) {
            return `Label(s) already exist: ${Array.from(new Set(conflictingLabels)).join(', ')}`;
        }
        
        return undefined;
    };

    const getSaveValuesDisabledTooltip = () => {
        if (!customer?.id) return undefined;
        if (valuesEdited.length === 0) return 'No changes to save';
        if (valuesEdited.find(value => value.value === '' || value.value === null)) return 'Value is required';
        
        // Check for duplicates
        const errorMessage = getSaveValuesErrorMessage();
        if (errorMessage) return errorMessage;
        
        return undefined;
    };

    const getSaveValuesErrorMessage = () => {
        if (!customer?.id) return undefined;
        
        let emptyValuesCount = 0;
        const seenValuesByTagIndex = new Map<string, Set<string>>();
        let duplicateErrorMessage: string | undefined;

        for (const value of localTagValues) {
            if (value.value === '' || value.value === null) {
                emptyValuesCount += 1;
                continue;
            }
            
            const tagIndexKey = value.tag_index.toString();
            const seenValues = seenValuesByTagIndex.get(tagIndexKey) ?? new Set<string>();
            
            const normalizedValue = value.value.trim().toLowerCase();
            if (seenValues.has(normalizedValue) && !duplicateErrorMessage) {
                const tagConfig = localTagConfigByIndex.get(String(value.tag_index));
                const tagName = tagConfig?.tag_label || `Tag ${value.tag_index}`;
                duplicateErrorMessage = `Duplicate value "${value.value}" found in ${tagName}`;
            }
            
            seenValues.add(normalizedValue);
            seenValuesByTagIndex.set(tagIndexKey, seenValues);
        }

        if (emptyValuesCount > 0) {
            return `${emptyValuesCount} tag value(s) have empty values`;
        }

        if (duplicateErrorMessage) return duplicateErrorMessage;
        
        return undefined;
    };


    return (
        <Stack
            direction='column'
            sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
        >
            <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} className={classes.tabs}>
                <Tab label="Tag Values" />
                <Tab label="Tag Configs" />
                <Tab label="Tag Documents" />
                <Tab label="Tag Groups" />
            </Tabs>

            <Stack
                direction='column'
                flex={1}
                className='tab-panel'
                sx={{ minHeight: 0, overflow: 'hidden' }}
            >
                {tabValue === 0 &&  
                <Stack
                    flex={1}
                    direction='column'
                    marginTop='16px'
                    className='tag-values-tab'
                    sx={{ minHeight: 0, overflow: 'hidden' }}
                >
                    <TagManagementButtons
                        customerId={customer?.id}
                        loading={tagValuesBusy || configsLoading}
                        edited={valuesEdited.length > 0}
                        onAdd={addNewValue}
                        onSave={handleSaveValues}
                        onRefresh={loadTagValues}
                        saveDisabledTooltip={getSaveValuesDisabledTooltip()}
                        saveErrorMessage={getSaveValuesErrorMessage()}
                        csvSelectProps={{
                            schema: {
                                fields: [
                                    { name: 'tag_label', required: true, type: 'string' },
                                    { name: 'value', required: true, type: 'string' }
                                ],
                                allowExtraFields: true,
                                minRows: 1,
                            },
                            onDataLoaded: handleCSVTagValueSelection,
                            tooltipText: 'Select tag values from CSV (tag_label and value columns)'
                        }}
                        csvDownloadProps={{
                            generateCSVContent: generateTagValuesCSV,
                            filename: "tag_values_template",
                            tooltipText: "Download CSV template",
                            validator: validateTagValuesDownload
                        }}
                        csvUploadProps={{
                            schema: {
                                fields: [
                                    { name: 'tag_label', required: true, type: 'string' },
                                    { name: 'value', required: true, type: 'string' },
                                    { name: 'is_active', required: false, type: 'boolean' },
                                    { name: 'description', required: false, type: 'string' }
                                ],
                                allowExtraFields: false
                            },
                            onDataLoaded: handleBulkUpload,
                            tooltipText: "Upload CSV to add tag values"
                        }}
                    />
                    <TagManagementDataGrid
                        rows={localTagValues}
                        columns={valueColumns}
                        loading={tagValuesBusy || configsLoading}
                        selectedIds={selectedValueIds}
                        getRowId={(row) => row.rowid}
                        onSelectionChange={setSelectedValueIds}
                        onRowChanged={onValueChanged}
                        disabled={!customer?.id || tagValuesBusy || configsLoading}
                        isRowSelectable={(params) => !isValueProtected(params.row as Tag)}
                        isCellEditableCustom={isValueCellEditableCustom}
                    />
                </Stack>}

                {tabValue === 1 && 
                <Stack
                    flex={1}
                    direction='column'
                    marginTop='16px'
                    className='tag-configs-tab'
                    sx={{ minHeight: 0, overflow: 'hidden' }}
                >
                    <TagManagementButtons
                        customerId={customer?.id}
                        loading={configsLoading}
                        edited={configsEdited.length > 0}
                        onAdd={addNewConfig}
                        onSave={saveConfigs}
                        onRefresh={loadTagConfigs}
                        addDisabledTooltip={getAddConfigDisabledTooltip()}
                        saveDisabledTooltip={getSaveConfigsDisabledTooltip()}
                        saveErrorMessage={getSaveConfigsErrorMessage()}
                    />

                    <TagManagementDataGrid
                        rows={localTagConfigs}
                        columns={configColumns}
                        loading={configsLoading || tagValuesBusy}
                        selectedIds={selectedConfigId ? [selectedConfigId] : []}
                        getRowId={(row) => row.tag_index.toString()}
                        onSelectionChange={(ids) => setSelectedConfigId(ids.length > 0 ? ids[0] : null)}
                        onRowChanged={onConfigChanged}
                        disabled={!customer?.id || configsLoading || tagValuesBusy}
                        isRowSelectable={(params) => {
                            const config = params.row as TagConfig;
                            if (!config) return false;
                            return !isConfigProtected(config);
                        }}
                        isCellEditableCustom={isConfigCellEditableCustom}
                        singleSelect={true}
                    />
                </Stack>
                }

                {tabValue === 2 &&
                <Stack
                    flex={1}
                    direction='column'
                    marginTop='16px'
                    className='tag-configs-tab'
                    sx={{ minHeight: 0, overflow: 'hidden' }}
                >
                    <TagManagementDocTags store={store} />
                </Stack>
                }

                {tabValue === 3 &&
                <Stack
                    flex={1}
                    direction='column'
                    marginTop='16px'
                    className='tag-groups-tab'
                    sx={{ minHeight: 0, overflow: 'hidden' }}
                >
                    <TagManagementButtons
                        customerId={customer?.id}
                        loading={groupsLoading || tagValuesBusy || configsLoading}
                        edited={groupsEdited.length > 0}
                        onAdd={addNewGroupRow}
                        addDisabledTooltip={!tagConfigs?.length ? 'Create tag configurations first' : undefined}
                        onSave={saveTagGroups}
                        saveDisabledTooltip={groupsEdited.length === 0 ? 'No unsaved changes' : undefined}
                        onDelete={handleDeleteTagGroupsClick}
                        onRefresh={loadTagValueGroups}
                        deleteDisabledTooltip={selectedGroupRowIds.length === 0 ? 'Select groups to delete' : undefined}
                        csvDownloadProps={{
                            generateCSVContent: generateTagGroupsCSV,
                            filename: 'tag_groups',
                            tooltipText: 'Download current tag groups',
                            validator: validateTagGroupsDownload,
                        }}
                        csvUploadProps={{
                            schema: { fields: [], allowExtraFields: true },
                            onDataLoaded: handleTagGroupsCSVUpload,
                            tooltipText: 'Upload CSV to replace all tag groups',
                        }}
                    />
                    <TagManagementDataGrid
                        rows={localTagGroupRows}
                        columns={tagGroupColumns}
                        loading={groupsLoading}
                        selectedIds={selectedGroupRowIds}
                        getRowId={(row) => row._id}
                        onSelectionChange={setSelectedGroupRowIds}
                        onRowChanged={onGroupRowChanged}
                        disabled={!customer?.id || groupsLoading}
                    />
                </Stack>
                }
            </Stack>

            {/* Confirmation Dialogs */}
            {dialogProps && <StepConfirmationWithState
                confirmationDialogs={[dialogProps]}
                finish={() => { }}
                cancel={() => setDialogProps(undefined)}
                isValid={true}
            />}
        </Stack>
    );
}); 